import { useCallback, useEffect, useMemo, useState } from 'react';
import Breadcrumbs from '../Components/Breadcrumbs';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { formatDateTimeLocal } from '../utils/formatDate';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button from '../Components/Buttons';
import DateRangeSelector, { getDateRangeByPeriod } from '../Components/DateRangeSelector';
import { Tab, Tabs } from '../Components/Tabs';
import { CustomSelect } from '../Components/CustomFormElements';
import SplitButton from '../Components/SplitButton';
import { useAlert } from '../Components/AlertContext';
import {
  AuditOperationsApiProvider,
  useAuditOperationsApi,
} from '../Services/AuditOperationsApiProvider';
import { LlmLogApiProvider, useLlmLogApi } from '../Services/LlmLogApiProvider';
import {
  ConnectionLogApiProvider,
  useConnectionLogApi,
} from '../Services/ConnectionLogApiProvider';
import { useAuth } from '../Contexts/AuthContext';
import useLatestRequest from '../Hooks/useLatestRequest';
import useTablePagination from '../Hooks/useTablePagination';
import { formatTableTitle } from '../utils/formatTitle';
import { humanizeKey } from '../utils/auditChanges';
import { getStoredFilter } from '../utils/storedFilter';
import { normalizeToArray, getSafeSelectedValues } from '../utils/array';

const resolveRange = (start, end) =>
  start ? { start_date: start, end_date: end } : getDateRangeByPeriod('today');

const TABS = {
  OPERATIONS: 'operations',
  LLM: 'llm',
  CONNECTIONS: 'connections',
};

const EMPTY_OPERATION_FILTERS = {
  record_type: [],
  action: [],
  status: [],
};

const OPERATION_STATUS_OPTIONS = ['success', 'error'].map((value) => ({
  label: humanizeKey(value),
  value,
}));

const splitOperationType = (operationType = '') => {
  const colon = operationType.indexOf(':');
  if (colon === -1) return [operationType, null];

  const topic = operationType.slice(0, colon);
  const subject = operationType.slice(colon + 1);
  const dot = subject.indexOf('.');
  return dot === -1 ? [topic, subject] : [subject.slice(0, dot), subject.slice(dot + 1)];
};

const formatOperationPart = (part) => humanizeKey(part.replace(/:/g, ' '));

const formatOperation = (operationType) => {
  const [recordType, action] = splitOperationType(operationType);
  return [recordType, action].filter(Boolean).map(formatOperationPart).join(' - ') || '-';
};

const toOptions = (values) =>
  [...new Set(values.filter(Boolean))]
    .sort()
    .map((value) => ({ label: formatOperationPart(value), value }));

const EMPTY_CONNECTION_LOG_FILTERS = {
  connection_name: [],
  connection_type: [],
  operation: [],
  status: [],
};

const formatPeriod = (row) => {
  const created = formatDateTimeLocal(row.created_at);
  const lastSeen = row.last_seen_at && formatDateTimeLocal(row.last_seen_at);
  return lastSeen && lastSeen !== created ? `${created} – ${lastSeen}` : created;
};

const AUDIT_COLUMNS = [
  { name: 'ID', selector: (row) => row.key, sortable: true },
  {
    name: 'User',
    selector: (row) => row.user_name ?? row.user_key ?? '-',
    sortable: true,
  },
  {
    name: 'Operation',
    selector: (row) => formatOperation(row.operation_type),
    sortable: true,
  },
  {
    name: 'Record',
    selector: (row) => row.record_name ?? row.record_key ?? '-',
    sortable: true,
  },
  {
    name: 'Period',
    selector: (row) => row.created_at,
    sortable: true,
    format: formatPeriod,
    grow: 2,
  },
  {
    name: 'Status',
    selector: (row) => (row.status ? humanizeKey(row.status) : '-'),
    sortable: true,
  },
];

const LLM_COLUMNS = [
  { name: 'Model', selector: (row) => row.model_name, sortable: true },
  { name: 'Total Tokens', selector: (row) => row.total_tokens, sortable: true },
  { name: 'Input Tokens', selector: (row) => row.input_tokens, sortable: true },
  { name: 'Output Tokens', selector: (row) => row.output_tokens, sortable: true },
  {
    name: 'Duration Input, s',
    selector: (row) => (row.duration_input ? Number(row.duration_input).toFixed(2) : '-'),
    sortable: true,
  },
  {
    name: 'Cost',
    selector: (row) => row.cost,
    sortable: true,
    format: (row) => {
      if (row.cost === null || row.cost === undefined) {
        return 'Not calculated';
      }

      return `${Number(row.cost).toFixed(2)} $`;
    },
  },
];

const LLM_REQUEST_COLUMNS = [
  { name: 'ID', selector: (row) => row.id, sortable: true },
  { name: 'Model', selector: (row) => row.model_name, sortable: true },
  { name: 'Usage Type', selector: (row) => row.usage_type, sortable: true },
  {
    name: 'Duration, s',
    selector: (row) => (row.duration != null ? Number(row.duration).toFixed(2) : '-'),
    sortable: true,
  },
  { name: 'Total Tokens', selector: (row) => row.total_tokens, sortable: true },
  { name: 'Input Tokens', selector: (row) => row.input_tokens, sortable: true },
  { name: 'Output Tokens', selector: (row) => row.output_tokens, sortable: true },
  {
    name: 'Duration Input, s',
    selector: (row) => (row.duration_input ? Number(row.duration_input).toFixed(2) : '-'),
    sortable: true,
  },
  { name: 'Created', selector: (row) => formatDateTimeLocal(row.created_at), sortable: true },
];

const CONNECTION_REQUEST_COLUMNS = [
  { name: 'ID', selector: (row) => row.id, sortable: true },
  { name: 'Connection', selector: (row) => row.connection_name, sortable: true },
  { name: 'Type', selector: (row) => row.connection_type, sortable: true },
  { name: 'Operation', selector: (row) => row.operation, sortable: true },
  { name: 'Status', selector: (row) => row.status, sortable: true },
  {
    name: 'Duration, s',
    selector: (row) => (row.duration != null ? Number(row.duration).toFixed(2) : '-'),
    sortable: true,
  },
  { name: 'Created', selector: (row) => formatDateTimeLocal(row.created_at), sortable: true },
];

function AuditOperationsHub({ canEdit }) {
  const { getAuditOperations, getAuditOperationTypes, exportAuditOperations } =
    useAuditOperationsApi();
  const { showAlert } = useAlert();
  const { organizationId } = useParams();
  const {
    getLlmLogs,
    getLlmLogModels,
    getLlmLogUsageTypes,
    getTokenAnalyticsByLLM,
    getTokenAnalytics,
  } = useLlmLogApi();
  const { getConnectionLogs, getConnectionLogFieldOptions } = useConnectionLogApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [auditOperations, setAuditOperations] = useState([]);
  const [totalAuditOperations, setTotalAuditOperations] = useState(0);
  const navigate = useNavigate();
  const { status: authStatus } = useAuth();

  const hasLlmLogFlag = authStatus?.feature_flags?.LLM_LOG === true;

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab =
    requestedTab === TABS.CONNECTIONS
      ? TABS.CONNECTIONS
      : hasLlmLogFlag && requestedTab === TABS.LLM
        ? TABS.LLM
        : TABS.OPERATIONS;

  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const [operationTypes, setOperationTypes] = useState(null);
  const [selectedOperations, setSelectedOperations] = useState([]);
  const [clearSelectedOperationsToggle, setClearSelectedOperationsToggle] = useState(false);

  const hasOrganizationsPermission = authStatus?.permissions?.includes('all:organizations');

  const [tokenAnalytics, setTokenAnalytics] = useState(null);
  const [tokenAnalyticsByLLM, setTokenAnalyticsByLLM] = useState([]);
  const [totalTokenCount, setTotalTokenCount] = useState(0);

  const [tokenStartDate, setTokenStartDate] = useState();
  const [tokenEndDate, setTokenEndDate] = useState();
  const [llmTokenStartDate, setLlmTokenStartDate] = useState();
  const [llmTokenEndDate, setLlmTokenEndDate] = useState();

  const [llmLogs, setLlmLogs] = useState([]);
  const [totalLlmLogs, setTotalLlmLogs] = useState(0);
  const [llmLogsLoading, setLlmLogsLoading] = useState(false);
  const [llmLogsError, setLlmLogsError] = useState(null);
  const [llmLogStartDate, setLlmLogStartDate] = useState();
  const [llmLogEndDate, setLlmLogEndDate] = useState();
  const [modelOptions, setModelOptions] = useState([]);
  const [usageTypeOptions, setUsageTypeOptions] = useState([]);

  const [connectionLogs, setConnectionLogs] = useState([]);
  const [totalConnectionLogs, setTotalConnectionLogs] = useState(0);
  const [connectionLogsLoading, setConnectionLogsLoading] = useState(false);
  const [connectionLogsError, setConnectionLogsError] = useState(null);
  const [connectionLogStartDate, setConnectionLogStartDate] = useState();
  const [connectionLogEndDate, setConnectionLogEndDate] = useState();

  const [connectionNameOptions, setConnectionNameOptions] = useState([]);
  const [connectionTypeOptions, setConnectionTypeOptions] = useState([]);
  const [connectionOperationOptions, setConnectionOperationOptions] = useState([]);
  const [connectionStatusOptions, setConnectionStatusOptions] = useState([]);

  const tableTitle = 'Audit Operations';
  const operationsPagination = useTablePagination(tableTitle);

  const tableTokensTitle = 'Models';
  const tokensPagination = useTablePagination(tableTokensTitle);

  const tableRequestsTitle = 'Requests';
  const requestsPagination = useTablePagination(tableRequestsTitle);

  const tableConnectionRequestsTitle = 'Connection Requests';
  const connectionRequestsPagination = useTablePagination(tableConnectionRequestsTitle);

  const beginOperationsRequest = useLatestRequest();
  const beginTokensByLlmRequest = useLatestRequest();
  const beginTokenTotalsRequest = useLatestRequest();
  const beginLlmLogsRequest = useLatestRequest();
  const beginConnectionLogsRequest = useLatestRequest();

  const searchFilterName = `${formatTableTitle(tableRequestsTitle)}_search_filter`;
  const usageFilterName = `${formatTableTitle(tableRequestsTitle)}_usage_filter`;
  const operationFiltersName = `${formatTableTitle(tableTitle)}_filters`;
  const connectionLogFiltersName = `${formatTableTitle(tableConnectionRequestsTitle)}_filters`;

  const [selectedModels, setSelectedModels] = useState(() => getStoredFilter(searchFilterName));
  const [selectedUsageTypes, setSelectedUsageTypes] = useState(() =>
    getStoredFilter(usageFilterName),
  );
  const [operationFilters, setOperationFilters] = useState(() => ({
    ...EMPTY_OPERATION_FILTERS,
    ...getStoredFilter(operationFiltersName),
  }));
  const [connectionLogFilters, setConnectionLogFilters] = useState(
    () => getStoredFilter(connectionLogFiltersName) ?? EMPTY_CONNECTION_LOG_FILTERS,
  );

  const hasActiveOperationFilters =
    (operationFilters.record_type?.length ?? 0) > 0 ||
    (operationFilters.action?.length ?? 0) > 0 ||
    (operationFilters.status?.length ?? 0) > 0;

  const hasActiveConnectionLogFilters =
    (connectionLogFilters.connection_name?.length ?? 0) > 0 ||
    (connectionLogFilters.connection_type?.length ?? 0) > 0 ||
    (connectionLogFilters.operation?.length ?? 0) > 0 ||
    (connectionLogFilters.status?.length ?? 0) > 0;

  const handleTabChange = useCallback(
    (value) => {
      const nextParams = new URLSearchParams(searchParams);
      if (value === TABS.OPERATIONS) nextParams.delete('tab');
      else nextParams.set('tab', value);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handlePeriodChange = useCallback((period) => {
    setStartDate(period?.start_date);
    setEndDate(period?.end_date);
  }, []);

  const operationTypeOptions = useMemo(
    () => toOptions((operationTypes ?? []).map((value) => splitOperationType(value)[0])),
    [operationTypes],
  );
  const operationActionOptions = useMemo(
    () => toOptions((operationTypes ?? []).map((value) => splitOperationType(value)[1])),
    [operationTypes],
  );

  const toOperationRequestFilters = ({ record_type, action, status }) => ({
    operation_types:
      record_type?.length || action?.length
        ? (operationTypes ?? []).filter((value) => {
            const [recordType, operationAction] = splitOperationType(value);
            return (
              (!record_type?.length || record_type.includes(recordType)) &&
              (!action?.length || action.includes(operationAction))
            );
          })
        : null,
    status: status?.length ? status : null,
  });

  const fetchAuditOperations = async (skip, limit, filters = operationFilters) => {
    const isLatest = beginOperationsRequest();
    try {
      setLoading(true);
      const requestFilters = toOperationRequestFilters(filters);
      if (requestFilters.operation_types?.length === 0) {
        setAuditOperations([]);
        setTotalAuditOperations(0);
        return;
      }
      const { start_date, end_date } = resolveRange(startDate, endDate);
      const response = await getAuditOperations(skip, limit, start_date, end_date, requestFilters);
      if (!isLatest()) return;
      setAuditOperations(response.items);
      setTotalAuditOperations(response.totalCount);
    } catch (error) {
      if (!isLatest()) return;
      console.error('Error fetching audit operations:', error);
      setError('Failed to load audit operations');
    } finally {
      if (isLatest()) setLoading(false);
    }
  };

  useEffect(() => {
    if (!startDate || !operationTypes) return;
    const { skip, limit } = operationsPagination.pageForFilters();
    fetchAuditOperations(skip, limit);
  }, [startDate, endDate, operationTypes]);

  useEffect(() => {
    getAuditOperationTypes()
      .then((types) => setOperationTypes(types ?? []))
      .catch(() => setOperationTypes([]));
  }, []);

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/audit-operations/${row.key}`);
  };

  const clearOperationSelection = () => {
    setSelectedOperations([]);
    setClearSelectedOperationsToggle((prev) => !prev);
  };

  const handleOperationFilterChange = (field, value) => {
    setOperationFilters((prev) => ({
      ...prev,
      [field]: normalizeToArray(value),
    }));
  };

  const applyOperationFilters = async () => {
    localStorage.setItem(operationFiltersName, JSON.stringify(operationFilters));
    clearOperationSelection();
    const { skip, limit } = operationsPagination.firstPage();
    await fetchAuditOperations(skip, limit, operationFilters);
  };

  const resetOperationFilters = async () => {
    localStorage.removeItem(operationFiltersName);
    setOperationFilters(EMPTY_OPERATION_FILTERS);
    clearOperationSelection();
    const { skip, limit } = operationsPagination.firstPage();
    await fetchAuditOperations(skip, limit, EMPTY_OPERATION_FILTERS);
  };

  const handleExportOperations = async (type) => {
    if (type === 'selected' && selectedOperations.length === 0) return;

    try {
      setLoading(true);
      const { start_date, end_date } = resolveRange(startDate, endDate);
      const exportParams =
        type === 'selected'
          ? { keys: selectedOperations.map((row) => row.key) }
          : { start_date, end_date, ...toOperationRequestFilters(operationFilters) };

      if (exportParams.operation_types?.length === 0) {
        showAlert('fail', 'No audit operations match the selected filters');
        return;
      }

      const file = await exportAuditOperations(exportParams);

      const url = window.URL.createObjectURL(file);

      const link = document.createElement('a');
      link.href = url;
      link.download = 'audit_operations.json';

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      if (type === 'selected') {
        clearOperationSelection();
      }
    } catch (error) {
      console.error(`Error exporting ${type} audit operations:`, error);
      showAlert('fail', `Failed to export ${type} audit operations!`);
    } finally {
      setLoading(false);
    }
  };

  const exportOperationsContextActions = (
    <SplitButton
      options={[
        {
          label: 'Export Selected',
          onClick: () => handleExportOperations('selected'),
        },
        {
          label: `Export all matching filter (${totalAuditOperations})`,
          onClick: () => handleExportOperations('filter'),
        },
      ]}
      disabled={selectedOperations.length === 0}
      variant="ghost"
      size="medium"
    />
  );

  const fetchTokenAnalyticsByLLM = async (skip, limit) => {
    const { start_date, end_date } = resolveRange(llmTokenStartDate, llmTokenEndDate);
    const isLatest = beginTokensByLlmRequest();

    try {
      const response = await getTokenAnalyticsByLLM(start_date, end_date, skip, limit);
      if (!isLatest()) return;
      setTokenAnalyticsByLLM(response.items || []);
      setTotalTokenCount(response.totalCount || 0);
    } catch (error) {
      console.error('Error fetching token analytics:', error);
    }
  };

  useEffect(() => {
    if (!llmTokenStartDate) return;
    if (hasLlmLogFlag) {
      const { skip, limit } = tokensPagination.pageForFilters();
      fetchTokenAnalyticsByLLM(skip, limit);
    }
  }, [
    llmTokenStartDate,
    llmTokenEndDate,
    getTokenAnalyticsByLLM,
    authStatus?.permissions,
    hasLlmLogFlag,
  ]);

  const fetchTokenAnalytics = async () => {
    const { start_date, end_date } = resolveRange(tokenStartDate, tokenEndDate);
    const isLatest = beginTokenTotalsRequest();
    try {
      const analyticsData = await getTokenAnalytics(start_date, end_date);
      if (!isLatest()) return;
      setTokenAnalytics(analyticsData);
    } catch (error) {
      console.error('Error fetching total analytics:', error);
    }
  };

  useEffect(() => {
    if (!hasLlmLogFlag || !tokenStartDate) return;
    fetchTokenAnalytics();
  }, [tokenStartDate, tokenEndDate, getTokenAnalytics, hasLlmLogFlag]);

  const handleLlmTokenPeriodChange = useCallback((period) => {
    setLlmTokenStartDate(period?.start_date);
    setLlmTokenEndDate(period?.end_date);
  }, []);

  const handleTokenPeriodChange = useCallback((period) => {
    setTokenStartDate(period?.start_date);
    setTokenEndDate(period?.end_date);
  }, []);

  const handleLlmLogPeriodChange = useCallback((period) => {
    setLlmLogStartDate(period?.start_date);
    setLlmLogEndDate(period?.end_date);
  }, []);

  const fetchLlmLogs = async (
    skip,
    limit,
    model = selectedModels,
    usageType = selectedUsageTypes,
  ) => {
    const isLatest = beginLlmLogsRequest();
    try {
      setLlmLogsLoading(true);
      const { start_date, end_date } = resolveRange(llmLogStartDate, llmLogEndDate);
      const response = await getLlmLogs(skip, limit, start_date, end_date, model, usageType);
      if (!isLatest()) return;
      setLlmLogs(response.items || []);
      setTotalLlmLogs(response.totalCount || 0);
      setLlmLogsError(null);
    } catch (error) {
      if (!isLatest()) return;
      console.error('Error fetching llm logs:', error);
      setLlmLogsError('Failed to load LLM requests');
    } finally {
      if (isLatest()) setLlmLogsLoading(false);
    }
  };

  useEffect(() => {
    if (!hasLlmLogFlag || llmLogStartDate === undefined || llmLogEndDate === undefined) {
      return;
    }
    const { skip, limit } = requestsPagination.pageForFilters();
    fetchLlmLogs(skip, limit);
  }, [llmLogStartDate, llmLogEndDate, hasLlmLogFlag]);

  useEffect(() => {
    if (!hasLlmLogFlag) return;
    const loadModels = async () => {
      try {
        const { start_date, end_date } = resolveRange(llmLogStartDate, llmLogEndDate);
        const models = await getLlmLogModels(start_date, end_date);
        setModelOptions((models || []).map((model) => ({ label: model, value: model })));
      } catch (error) {
        console.error('Error fetching llm models:', error);
      }
    };
    loadModels();
  }, [llmLogStartDate, llmLogEndDate, getLlmLogModels, hasLlmLogFlag]);

  useEffect(() => {
    if (!hasLlmLogFlag) return;
    const loadUsageTypes = async () => {
      try {
        const { start_date, end_date } = resolveRange(llmLogStartDate, llmLogEndDate);
        const usageTypes = await getLlmLogUsageTypes(start_date, end_date);
        setUsageTypeOptions(
          (usageTypes || []).map((usageType) => ({ label: usageType, value: usageType })),
        );
      } catch (error) {
        console.error('Error fetching llm usage types:', error);
      }
    };
    loadUsageTypes();
  }, [llmLogStartDate, llmLogEndDate, getLlmLogUsageTypes, hasLlmLogFlag]);

  const handleModelChange = async (skip, limit, model) => {
    const nextModels = normalizeToArray(model);
    const safeModels = getSafeSelectedValues(modelOptions, nextModels);

    localStorage.setItem(searchFilterName, JSON.stringify(safeModels));
    setSelectedModels(safeModels);
    await fetchLlmLogs(0, limit, safeModels, selectedUsageTypes);
  };

  const handleUsageTypeChange = async (usageType) => {
    const nextUsageTypes = normalizeToArray(usageType);
    const safeUsageTypes = getSafeSelectedValues(usageTypeOptions, nextUsageTypes);

    localStorage.setItem(usageFilterName, JSON.stringify(safeUsageTypes));
    setSelectedUsageTypes(safeUsageTypes);
    const { skip, limit } = requestsPagination.firstPage();
    await fetchLlmLogs(skip, limit, selectedModels, safeUsageTypes);
  };

  const llmRequestUrl = (row) =>
    `/organizations/${organizationId}/audit-operations/llm-requests/${row.id}`;

  const handleLlmLogRowClicked = (row) => {
    navigate(llmRequestUrl(row));
  };

  const handleConnectionLogPeriodChange = useCallback((period) => {
    setConnectionLogStartDate(period?.start_date);
    setConnectionLogEndDate(period?.end_date);
  }, []);

  const fetchConnectionLogs = async (skip, limit, filters = connectionLogFilters) => {
    const isLatest = beginConnectionLogsRequest();
    try {
      setConnectionLogsLoading(true);
      const { start_date, end_date } = resolveRange(connectionLogStartDate, connectionLogEndDate);
      const response = await getConnectionLogs(skip, limit, start_date, end_date, filters);
      if (!isLatest()) return;
      setConnectionLogs(response.items || []);
      setTotalConnectionLogs(response.totalCount || 0);
      setConnectionLogsError(null);
    } catch (error) {
      if (!isLatest()) return;
      console.error('Error fetching connection logs:', error);
      setConnectionLogsError('Failed to load connection requests');
    } finally {
      if (isLatest()) setConnectionLogsLoading(false);
    }
  };

  useEffect(() => {
    if (!connectionLogStartDate) return;
    const { skip, limit } = connectionRequestsPagination.pageForFilters();
    fetchConnectionLogs(skip, limit);
  }, [connectionLogStartDate, connectionLogEndDate]);

  useEffect(() => {
    const loadConnectionLogFilterOptions = async () => {
      try {
        const [connections, types, operations, statuses] = await Promise.all([
          getConnectionLogFieldOptions('connection_name'),
          getConnectionLogFieldOptions('connection_type'),
          getConnectionLogFieldOptions('operation'),
          getConnectionLogFieldOptions('status'),
        ]);
        setConnectionNameOptions((connections || []).map((value) => ({ label: value, value })));
        setConnectionTypeOptions((types || []).map((value) => ({ label: value, value })));
        setConnectionOperationOptions((operations || []).map((value) => ({ label: value, value })));
        setConnectionStatusOptions((statuses || []).map((value) => ({ label: value, value })));
      } catch (error) {
        console.error('Error fetching connection log filter options:', error);
      }
    };
    loadConnectionLogFilterOptions();
  }, []);

  const handleConnectionLogFilterChange = (field, value) => {
    setConnectionLogFilters((prev) => ({
      ...prev,
      [field]: normalizeToArray(value),
    }));
  };

  const applyConnectionLogFilters = async () => {
    localStorage.setItem(connectionLogFiltersName, JSON.stringify(connectionLogFilters));
    const { skip, limit } = connectionRequestsPagination.firstPage();
    await fetchConnectionLogs(skip, limit, connectionLogFilters);
  };

  const resetConnectionLogFilters = async () => {
    localStorage.removeItem(connectionLogFiltersName);
    setConnectionLogFilters(EMPTY_CONNECTION_LOG_FILTERS);
    const { skip, limit } = connectionRequestsPagination.firstPage();
    await fetchConnectionLogs(skip, limit, EMPTY_CONNECTION_LOG_FILTERS);
  };

  const connectionRequestUrl = (row) =>
    `/organizations/${organizationId}/audit-operations/connection-requests/${row.id}`;

  const handleConnectionLogRowClicked = (row) => {
    navigate(connectionRequestUrl(row));
  };

  return (
    <div className="container">
      <Breadcrumbs />
      <Tabs activeTab={activeTab} onTabChange={handleTabChange}>
        <Tab label="Operations" value={TABS.OPERATIONS}>
          <div className="row d-flex gap">
            <div className="radius-yes p-2 bg-yes col-12">
              <div className="p-2 border-yes radius-yes mb-3">
                <h4 className="h4-title">Filters</h4>
                <div className="gap d-flex folded">
                  <div className="filter-col">
                    <CustomSelect
                      id="audit-operation-type-filter"
                      label="Type"
                      options={operationTypeOptions}
                      value={operationFilters.record_type}
                      multiSelect={true}
                      onChange={(value) => handleOperationFilterChange('record_type', value)}
                      placeholder="Select types"
                    />
                  </div>

                  <div className="filter-col">
                    <CustomSelect
                      id="audit-operation-action-filter"
                      label="Operation"
                      options={operationActionOptions}
                      value={operationFilters.action}
                      multiSelect={true}
                      onChange={(value) => handleOperationFilterChange('action', value)}
                      placeholder="Select operations"
                    />
                  </div>

                  <div className="filter-col">
                    <CustomSelect
                      id="audit-operation-status-filter"
                      label="Status"
                      options={OPERATION_STATUS_OPTIONS}
                      value={operationFilters.status}
                      multiSelect={true}
                      onChange={(value) => handleOperationFilterChange('status', value)}
                      placeholder="Select statuses"
                    />
                  </div>
                </div>

                <div className="d-flex gap mt-2">
                  <Button size="medium" background="color2" onClick={applyOperationFilters}>
                    Apply filters
                  </Button>
                  <Button
                    size="medium"
                    variant="ghost"
                    onClick={resetOperationFilters}
                    disabled={!hasActiveOperationFilters}
                  >
                    Reset filters
                  </Button>
                </div>
              </div>

              {error ? (
                <div>Error: {error}</div>
              ) : (
                <Table
                  title={tableTitle}
                  columns={AUDIT_COLUMNS}
                  data={auditOperations}
                  selectableRows
                  selectableRowsHighlight
                  singleSelect={false}
                  clearSelectedRows={clearSelectedOperationsToggle}
                  hideDeleteSelected
                  progressPending={loading}
                  paginationPerPage={operationsPagination.initialPageSize}
                  resetPaginationToggle={operationsPagination.resetToggle}
                  onRowClicked={handleRowClicked}
                  paginationTotalRows={totalAuditOperations}
                  onChangePage={(skip, limit) => fetchAuditOperations(skip, limit)}
                  getRowUrl={(row) =>
                    `/organizations/${organizationId}/audit-operations/${row.key}`
                  }
                  onSelectedRowsChange={({ selectedRows }) => setSelectedOperations(selectedRows)}
                  contextActionsExtra={exportOperationsContextActions}
                />
              )}
              <DateRangeSelector storageKey="audit-operations" onChange={handlePeriodChange} />
            </div>
          </div>
        </Tab>

        {hasLlmLogFlag && (
          <Tab label="LLM" value={TABS.LLM}>
            <div className="row d-flex gap">
              {hasOrganizationsPermission && (
                <div className="radius-yes p-2 bg-yes col-12">
                  <h2 style={{ fontWeight: 'normal' }}>Total Analytics</h2>
                  {tokenAnalytics?.total_tokens == null ? (
                    <p>No token analytics data available.</p>
                  ) : (
                    <div className="analytics-card" style={{ maxWidth: '500px' }}>
                      <div className="analytics-header">
                        <h3>Total Tokens</h3>
                      </div>
                      <div className="analytics-value">
                        {tokenAnalytics.total_tokens.toLocaleString() || 0}
                      </div>
                      <div className="analytics-details">
                        <div className="detail-item">
                          <span className="label">Input tokens</span>
                          <span className="value">
                            {tokenAnalytics.input_tokens?.toLocaleString() || 0}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Output tokens</span>
                          <span className="value">
                            {tokenAnalytics.output_tokens?.toLocaleString() || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div style={{ marginTop: '6vh' }}>
                    <DateRangeSelector
                      storageKey="audit-total"
                      onChange={handleTokenPeriodChange}
                    />
                  </div>
                </div>
              )}

              <div className="radius-yes p-2 bg-yes col-12">
                {!tokenAnalyticsByLLM.length ? (
                  <p>No LLM analytics data available.</p>
                ) : (
                  <Table
                    title={tableTokensTitle}
                    columns={LLM_COLUMNS}
                    data={tokenAnalyticsByLLM}
                    selectableRowsHighlight
                    paginationPerPage={tokensPagination.initialPageSize}
                    resetPaginationToggle={tokensPagination.resetToggle}
                    paginationTotalRows={totalTokenCount}
                    onChangePage={fetchTokenAnalyticsByLLM}
                  />
                )}
                <DateRangeSelector storageKey="audit-llm" onChange={handleLlmTokenPeriodChange} />
              </div>

              <div className="radius-yes p-2 bg-yes col-12">
                {llmLogsError ? (
                  <div>Error: {llmLogsError}</div>
                ) : (
                  <Table
                    title={tableRequestsTitle}
                    columns={LLM_REQUEST_COLUMNS}
                    data={llmLogs}
                    selectableRowsHighlight
                    progressPending={llmLogsLoading}
                    paginationPerPage={requestsPagination.initialPageSize}
                    paginationTotalRows={totalLlmLogs}
                    resetPaginationToggle={requestsPagination.resetToggle}
                    onRowClicked={handleLlmLogRowClicked}
                    onChangePage={fetchLlmLogs}
                    getRowUrl={llmRequestUrl}
                    onChangeFilter={handleModelChange}
                    typeSearch="multiselect"
                    searchOptions={modelOptions}
                    defaultValue={selectedModels}
                    headerActions={
                      usageTypeOptions.length ? (
                        <div
                          className="input-block d-flex flex-row justify-between"
                          style={{ flex: '0 0 auto', width: 320 }}
                        >
                          <CustomSelect
                            id="llm-usage-type-filter"
                            label="Usage Type"
                            options={usageTypeOptions}
                            value={selectedUsageTypes}
                            multiSelect={true}
                            onChange={handleUsageTypeChange}
                            placeholder="Select usage types"
                          />
                        </div>
                      ) : null
                    }
                  />
                )}
                <DateRangeSelector
                  storageKey="audit-llm-requests"
                  onChange={handleLlmLogPeriodChange}
                />
              </div>
            </div>
          </Tab>
        )}

        <Tab label="Connections" value={TABS.CONNECTIONS}>
          <div className="row d-flex gap">
            <div className="radius-yes p-2 bg-yes col-12">
              <div className="p-2 border-yes radius-yes mb-3">
                <h4 className="h4-title">Filters</h4>
                <div className="gap d-flex folded">
                  <div className="filter-col">
                    <CustomSelect
                      id="connection-log-connection-filter"
                      label="Connection"
                      options={connectionNameOptions}
                      value={connectionLogFilters.connection_name}
                      multiSelect={true}
                      onChange={(value) =>
                        handleConnectionLogFilterChange('connection_name', value)
                      }
                      placeholder="Select connections"
                    />
                  </div>

                  <div className="filter-col">
                    <CustomSelect
                      id="connection-log-type-filter"
                      label="Type"
                      options={connectionTypeOptions}
                      value={connectionLogFilters.connection_type}
                      multiSelect={true}
                      onChange={(value) =>
                        handleConnectionLogFilterChange('connection_type', value)
                      }
                      placeholder="Select types"
                    />
                  </div>

                  <div className="filter-col">
                    <CustomSelect
                      id="connection-log-operation-filter"
                      label="Operation"
                      options={connectionOperationOptions}
                      value={connectionLogFilters.operation}
                      multiSelect={true}
                      onChange={(value) => handleConnectionLogFilterChange('operation', value)}
                      placeholder="Select operations"
                    />
                  </div>

                  <div className="filter-col">
                    <CustomSelect
                      id="connection-log-status-filter"
                      label="Status"
                      options={connectionStatusOptions}
                      value={connectionLogFilters.status}
                      multiSelect={true}
                      onChange={(value) => handleConnectionLogFilterChange('status', value)}
                      placeholder="Select statuses"
                    />
                  </div>
                </div>

                <div className="d-flex gap mt-2">
                  <Button size="medium" background="color2" onClick={applyConnectionLogFilters}>
                    Apply filters
                  </Button>
                  <Button
                    size="medium"
                    variant="ghost"
                    onClick={resetConnectionLogFilters}
                    disabled={!hasActiveConnectionLogFilters}
                  >
                    Reset filters
                  </Button>
                </div>
              </div>

              {connectionLogsError ? (
                <div>Error: {connectionLogsError}</div>
              ) : (
                <Table
                  title={tableConnectionRequestsTitle}
                  columns={CONNECTION_REQUEST_COLUMNS}
                  data={connectionLogs}
                  selectableRowsHighlight
                  progressPending={connectionLogsLoading}
                  paginationPerPage={connectionRequestsPagination.initialPageSize}
                  paginationTotalRows={totalConnectionLogs}
                  resetPaginationToggle={connectionRequestsPagination.resetToggle}
                  onRowClicked={handleConnectionLogRowClicked}
                  onChangePage={fetchConnectionLogs}
                  getRowUrl={connectionRequestUrl}
                />
              )}
              <DateRangeSelector
                storageKey="audit-connection-requests"
                onChange={handleConnectionLogPeriodChange}
              />
            </div>
          </div>
        </Tab>
      </Tabs>
    </div>
  );
}

function AuditOperationsWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <AuditOperationsApiProvider>
        <LlmLogApiProvider>
          <ConnectionLogApiProvider>
            <AuditOperationsHub canEdit={canEdit} />
          </ConnectionLogApiProvider>
        </LlmLogApiProvider>
      </AuditOperationsApiProvider>
    </ConfirmationProvider>
  );
}

export default AuditOperationsWrapper;
